import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack
} from '@mui/material';
import MoreMenuButton from 'components/util/MoreMenuButton';
import { legacyRoleRules } from 'access';
import useCustomDispatch from 'redux/dispatch';
import { Button, InfoTooltip } from '@nps.today/component-library';
import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslator } from 'translation/useTranslator';
import {
  ExternalAuthentication,
  ExternalAuthenticationNotificationTriggerSummary
} from '../../../../@types/externalAuthentication';
import {
  deleteExternalAuthentication,
  saveExternalAuthentication
} from 'redux/slices/externalAuthentication';
import { ExternalAuthenticationMethodForm } from './ExternalAuthenticationMethodForm';
import { Text } from 'components/text/Text';
import styles from './ExternalAuthenticationMethods.module.css';
import { InfoDialog, InfoDialogItem } from 'components/dialog/InfoDialog';

interface ExternalAuthenticationMethodRowProps {
  externalAuthentication: ExternalAuthentication;
}

export const ExternalAuthenticationMethodRow: FunctionComponent<
  ExternalAuthenticationMethodRowProps
> = ({ externalAuthentication }) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const [editOpen, setEditOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedExternalAuthentication, setSelectedExternalAuthentication] =
    useState<ExternalAuthentication>(externalAuthentication);

  return (
    <>
      <Stack direction="row" alignItems="center" key={externalAuthentication.id} gap={2}>
        <Box sx={{ flexGrow: 1, ml: 2, minWidth: 100 }}>
          <Text variant="subtitle2" noWrap>
            {externalAuthentication.name}
          </Text>
          <Text variant="caption" sx={{ color: 'text.secondary' }} noWrap>
            <b>{`${translator.type()}`}</b>: {externalAuthentication.type}
          </Text>
          <Stack>
            <Text variant="caption" color="text.secondary" noWrap>
              {translator.linkedToXNotificationTriggers(
                `${externalAuthentication.linkedNotificationTriggers.length}`
              )}
            </Text>
            <Text variant="caption" color="text.secondary" noWrap>
              {translator.linkedToXWebhooks(`${externalAuthentication.linkedWebHooks.length}`)}
            </Text>
          </Stack>
        </Box>
        <MoreMenuButton
          itemType=""
          onEdit={() => {
            setSelectedExternalAuthentication(externalAuthentication);
            setEditOpen(true);
          }}
          onEditAccess={legacyRoleRules.adminManage}
          onDelete={() => {
            customDispatch({
              action: deleteExternalAuthentication,
              actionParameters: externalAuthentication.id
            });
          }}
          onDeleteAccess={legacyRoleRules.adminManage}
          deleteAreYouSureContent={
            <DeleteContent externalAuthentication={externalAuthentication} />
          }
          onInfo={() => {
            setSelectedExternalAuthentication(externalAuthentication);
            setInfoOpen(true);
          }}
        />
      </Stack>
      <EditDialog
        externalAuthentication={selectedExternalAuthentication}
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onFormChanges={setSelectedExternalAuthentication}
        onEdit={() =>
          customDispatch({
            action: saveExternalAuthentication,
            actionParameters: {
              externalAuthentication: selectedExternalAuthentication
            },
            onSuccess: () => {
              setEditOpen(false);
            }
          })
        }
      />
      <InformationDialog
        externalAuthentication={selectedExternalAuthentication}
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
      />
    </>
  );
};

interface EditDialogProps {
  externalAuthentication: ExternalAuthentication;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (externalAuthentication: ExternalAuthentication) => void;
  onFormChanges: (externalAuthentication: ExternalAuthentication) => void;
}

const EditDialog = ({
  externalAuthentication,
  isOpen,
  onClose,
  onEdit,
  onFormChanges
}: EditDialogProps) => {
  const translator = useTranslator();
  const editDisabled = useMemo(() => {
    const authValueMissing = externalAuthentication.value?.length <= 0;
    const nameMissing = externalAuthentication.name?.length <= 0;
    const basicAuthUsernameMissing =
      externalAuthentication.type === 'Basic' &&
      (!externalAuthentication.basicAuthUsername ||
        externalAuthentication.basicAuthUsername?.length <= 0);
    const customAuthHeaderNameMissing =
      externalAuthentication.type === 'CustomAuthHeader' &&
      (!externalAuthentication.customAuthenticationHeaderName ||
        externalAuthentication.customAuthenticationHeaderName.length <= 0);
    return (
      authValueMissing || nameMissing || basicAuthUsernameMissing || customAuthHeaderNameMissing
    );
  }, [externalAuthentication]);
  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth>
      <DialogTitle>{translator.editExternalAuthenticationMethod()}</DialogTitle>
      <DialogContent>
        <ExternalAuthenticationMethodForm
          externalAuthentication={externalAuthentication}
          onUpdate={onFormChanges}
        />
      </DialogContent>
      <DialogActions>
        <Button dataTestId="edit-external-authentication-method-dialog-cancel" onClick={onClose}>
          {translator.cancel()}
        </Button>
        <Button
          dataTestId="edit-external-authentication-method-dialog-edit"
          onClick={() => onEdit(externalAuthentication)}
          autoFocus
          disabled={editDisabled}
        >
          {translator.edit()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface InformationDialogProps {
  externalAuthentication: ExternalAuthentication;
  isOpen: boolean;
  onClose: () => void;
}

const InformationDialog = ({ externalAuthentication, isOpen, onClose }: InformationDialogProps) => {
  const translator = useTranslator();
  const items: InfoDialogItem[] = useMemo(() => {
    return [
      { label: translator.name(), value: externalAuthentication.name },
      { label: translator.type(), value: externalAuthentication.type },
      {
        label: translator.linkedNotificationTriggers(),
        furtherInfo: translator.linkedNotificationTriggersHelperText(),
        value: (
          <LinkedNotificationTriggers
            linkedNotificationTriggers={externalAuthentication.linkedNotificationTriggers}
          />
        )
      },
      {
        label: translator.linkedWebhooks(),
        value:
          externalAuthentication.linkedWebHooks.length > 0 ? (
            externalAuthentication.linkedWebHooks.map((webhookId) => (
              <Text key={webhookId} variant="body2">
                • {webhookId}
              </Text>
            ))
          ) : (
            <Text variant="body2">{translator.noLinkedWebhooks()}</Text>
          )
      }
    ];
  }, [
    externalAuthentication.linkedNotificationTriggers,
    externalAuthentication.linkedWebHooks,
    externalAuthentication.name,
    externalAuthentication.type,
    translator
  ]);

  return (
    <InfoDialog
      items={items}
      isOpen={isOpen}
      onClose={onClose}
      dataTestId="external-authentication-method-info-dialog"
    />
  );
};

interface LinkedNotificationTriggersProps {
  linkedNotificationTriggers: ExternalAuthenticationNotificationTriggerSummary[];
}

const LinkedNotificationTriggers: FunctionComponent<LinkedNotificationTriggersProps> = ({
  linkedNotificationTriggers
}) => {
  const translator = useTranslator();
  return linkedNotificationTriggers.length > 0 ? (
    <>
      {linkedNotificationTriggers.map((trigger) => (
        <Link
          href={`/home/campaigns/${trigger.campaignId}`}
          target="_blank"
          key={trigger.id}
          underline="none"
        >
          <Text key={trigger.campaignId} variant="body2">
            •{' '}
            {trigger.campaignName ? trigger.campaignName : `/home/campaigns/${trigger.campaignId}`}
          </Text>
        </Link>
      ))}
    </>
  ) : (
    <Text variant="body2">{translator.noLinkedNotificationTriggers()}</Text>
  );
};

interface DeleteContentProps {
  externalAuthentication: ExternalAuthentication;
}

const DeleteContent: FunctionComponent<DeleteContentProps> = ({ externalAuthentication }) => {
  const translator = useTranslator();
  const anyLinkedNotificationTriggers =
    externalAuthentication.linkedNotificationTriggers.length > 0;
  const anyLinkedWebhooks = externalAuthentication.linkedWebHooks.length > 0;
  const anyLinkedEntities = anyLinkedNotificationTriggers || anyLinkedWebhooks;
  return (
    <Stack gap={1}>
      <Text>
        {translator.areYouSureYouWantToDeleteThisAuthenticationMethod(externalAuthentication.name)}
      </Text>
      {externalAuthentication.linkedNotificationTriggers.length > 0 ||
      externalAuthentication.linkedWebHooks.length > 0 ? (
        <Alert severity="warning">
          <Text variant="body2">
            {translator.deleteExternalAuthenticationLinkedEntitiesWarning()}
          </Text>
        </Alert>
      ) : null}
      {anyLinkedEntities && (
        <div className={styles.linkedEntitiesContainer}>
          {anyLinkedNotificationTriggers && (
            <Stack gap={1}>
              <Text variant="subtitle2" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {translator.linkedNotificationTriggers()}
                <InfoTooltip
                  description={translator.linkedNotificationTriggersHelperText()}
                  isText
                />
              </Text>
              <LinkedNotificationTriggers
                linkedNotificationTriggers={externalAuthentication.linkedNotificationTriggers}
              />
            </Stack>
          )}
          {anyLinkedWebhooks && (
            <Stack gap={1}>
              <Text variant="subtitle2">{translator.linkedWebhooks()}</Text>
              {externalAuthentication.linkedWebHooks.map((webhookId) => (
                <Text key={webhookId} variant="body2">
                  • {webhookId}
                </Text>
              ))}
            </Stack>
          )}
        </div>
      )}
    </Stack>
  );
};
